import { ComponentProps, useEffect, useRef, useState } from 'react';
import { PlugsConnected, Plugs } from '@phosphor-icons/react';
import { Application, Container, Graphics, Ticker } from 'pixi.js';

export type ConnectButtonProps = ComponentProps<'button'> & {
  connected: boolean;
};

export function ConnectButton(props: ConnectButtonProps) {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const { onClick, connected, ...rest } = props;
  return (
    <button
      onClick={(e) => {
        onClick?.(e);
      }}
      style={{ position: 'relative', width: '25px', height: '25px' }}
      {...rest}
    >
      <div
        ref={setRef}
        style={{
          width: '400%',
          height: '400%',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }}
      >
        {ref && <Sparks shouldEmit={props.connected} containerRef={ref} />}
        {props.connected ? (
          <PlugsConnected
            size={14}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        ) : (
          <Plugs
            size={14}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        )}
      </div>
    </button>
  );
}

class SparksContainer {
  container: Container;
  sparks: {
    graphics: Graphics;
    velocityX: number;
    velocityY: number;
    speed: number;
    delay: number;
    age: number;
    maxAge: number;
    gravity: number;
  }[] = [];
  elapsed = 0;
  shouldEmit = false;
  ticker: Ticker;

  constructor(app: Application) {
    this.container = new Container();

    this.animate = this.animate.bind(this);

    this.ticker = app.ticker.add(this.update.bind(this));
    this.ticker.stop();
  }

  animate() {
    this.elapsed = 0;
    this.sparks = [];
    for (let i = 0; i < 50; i++) {
      const color = 0xfbe900;
      const size = Math.random() * 0.5 + 1.5;
      const circle = new Graphics().circle(0, 0, size).fill(color);

      // Calculate initial direction - mostly upward but with spread
      const angle = Math.random() * Math.PI * 0.8 - Math.PI * 0.4; // Range from -0.4π to 0.4π

      const speed = Math.random() * 3 + 2;

      this.sparks.push({
        graphics: circle,
        velocityX: Math.sin(angle) * speed, // Using sin for X since angle is relative to vertical
        velocityY: -Math.cos(angle) * speed, // Using negative cos for Y to go upward initially
        speed: speed,
        delay: Math.random() * 200,
        age: 0,
        maxAge: Math.random() * 40 + 20,
        gravity: Math.random() * 0.15 + 0.1 // Add gravity to pull sparks down
      });
      this.container.addChild(circle);
      circle.visible = false;
    }

    this.ticker.start();
  }
  update(ticker: Ticker) {
    this.elapsed += ticker.deltaMS;

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      if (!spark) {
        continue;
      }
      if (this.elapsed < spark.delay) {
        spark.graphics.visible = false;
        return;
      }

      if (spark.age > spark.maxAge) {
        spark.age = 0;
        spark.graphics.x = 0;
        spark.graphics.y = 0;

        this.container.removeChild(spark.graphics);
        this.sparks.splice(i, 1);
        return;
      }

      spark.graphics.visible = true;

      // Apply gravity to Y velocity
      spark.velocityY += spark.gravity;

      // Move spark according to velocity
      spark.graphics.x += spark.velocityX;
      spark.graphics.y += spark.velocityY;

      // Rotate spark to face direction of movement
      const direction = Math.atan2(spark.velocityY, spark.velocityX);
      spark.graphics.rotation = direction;

      // Fade out as spark ages
      spark.graphics.alpha = 1 - spark.age / spark.maxAge;

      // Increase age
      spark.age++;
    }

    if (this.sparks.length === 0) {
      this.shouldEmit = false;
      this.ticker.stop();
    }
  }
}

function Sparks({
  shouldEmit = false,
  containerRef
}: {
  shouldEmit: boolean;
  containerRef: HTMLDivElement | null;
}) {
  const sparksRef = useRef<SparksContainer | null>(null);
  const appInitialized = useRef(false);
  const [app, setApp] = useState<Application | null>(null);
  async function createApp() {
    if (containerRef) {
      const app = new Application();
      await app.init({ backgroundAlpha: 0, resizeTo: containerRef });
      containerRef.appendChild(app.canvas);
      console.log('App created');
      const sparks = new SparksContainer(app);
      app.stage.addChild(sparks.container);
      sparks.container.x = app.screen.width / 2;
      sparks.container.y = app.screen.height / 2;
      sparksRef.current = sparks;
      setApp(app);
    }
  }

  useEffect(() => {
    createApp();
  }, [setApp]);

  useEffect(() => {
    return () => {
      if (app && appInitialized.current) {
        console.log('Destroying app');
        app.destroy(true, { children: true });
        setApp(null);
      }
    };
  }, [app]);

  // useEffect(() => {
  //     // Initialize the application
  //     return () => {
  //         if (app) {
  //         }
  //     }
  // }, [app])

  useEffect(() => {
    if (sparksRef.current) {
      sparksRef.current.shouldEmit = shouldEmit;
      if (shouldEmit) {
        sparksRef.current.animate();
      }
    }
  }, [shouldEmit]);

  return null;
}
